import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Lightbulb as PromptIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Assessment as AssessmentIcon,
  FilterAlt as FilterIcon,
  Map as MapIcon,
  HelpOutline as HelpIcon,
  DataObject as DataIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useWebMapContext } from '../webMap/WebMapContext';
import type { WidgetDataBinding } from '../types/webMap';

export interface SpatialAnalyticsWidgetProps {
  widgetConfig?: {
    dataBinding?: WidgetDataBinding;
    spatialInstructions?: string;
  };
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

function hexToRgb(hex: string): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}` : '76, 175, 80';
}

function renderChatContent(content: string): React.ReactNode[] {
  const lines = content.split('\n');
  return lines.flatMap((line, lineIndex) => {
    const parts = line.split(/(\*\*.*?\*\*)/g).filter(Boolean);
    const rendered = parts.map((part, partIndex) => {
      const isBold = part.startsWith('**') && part.endsWith('**');
      const text = isBold ? part.slice(2, -2) : part;
      return isBold
        ? <strong key={`${lineIndex}-${partIndex}`}>{text}</strong>
        : <React.Fragment key={`${lineIndex}-${partIndex}`}>{text}</React.Fragment>;
    });
    return lineIndex < lines.length - 1
      ? [...rendered, <br key={`br-${lineIndex}`} />]
      : rendered;
  });
}

function buildFieldSummaries(
  features: GeoJSON.Feature[],
  schemas: Map<string, { name: string; type: string }[]>,
  layerId: string,
) {
  const cols = schemas.get(layerId) ?? [];
  const summaries: Array<Record<string, unknown>> = [];

  for (const col of cols) {
    if (col.name === 'geom' || col.name === 'geometry' || col.name === 'the_geom' || col.name === 'ogc_fid' || col.name === 'gid') continue;

    const values = features.map((f) => f.properties?.[col.name]).filter((v) => v != null);
    if (values.length === 0) continue;

    const isNumeric = ['integer', 'bigint', 'numeric', 'real', 'double precision', 'smallint', 'float4', 'float8', 'int4', 'int8'].includes(col.type);

    if (isNumeric) {
      const nums = values.map(Number).filter(Number.isFinite);
      if (nums.length === 0) continue;
      summaries.push({
        field: col.name,
        type: 'number',
        min: Math.min(...nums),
        max: Math.max(...nums),
        avg: nums.reduce((a, b) => a + b, 0) / nums.length,
        uniqueValues: new Set(nums).size,
      });
    } else {
      const counts = new Map<string, number>();
      for (const v of values) {
        const k = String(v);
        counts.set(k, (counts.get(k) ?? 0) + 1);
      }
      const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
      summaries.push({
        field: col.name,
        type: 'string',
        uniqueValues: counts.size,
        topValues: sorted.slice(0, 8).map(([value, count]) => ({ value, count })),
      });
    }
  }
  return summaries;
}

function detectGeometryType(features: GeoJSON.Feature[]): string {
  const types = new Set(features.map((f) => f.geometry?.type).filter(Boolean));
  if (types.size === 0) return 'Unknown';
  const arr = Array.from(types);
  if (arr.length === 1) return arr[0]!;
  return arr.join(', ');
}

function tryParseAction(text: string): { action: string; [k: string]: unknown } | null {
  const match = text.match(/```json\s*(\{[\s\S]*?\})\s*```/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1]);
    if (parsed && typeof parsed.action === 'string') return parsed;
  } catch { /* ignore */ }
  return null;
}

const PREDEFINED_PROMPTS = [
  {
    category: 'Getting Started',
    icon: <HelpIcon fontSize="small" />,
    prompts: [
      { label: 'Summarise this dataset', prompt: 'Give me a summary of this dataset' },
      { label: 'What fields are available?', prompt: 'What fields are available and what do they contain?' },
      { label: 'What can you help with?', prompt: 'What kinds of analysis can you help me with on this spatial data?' },
    ],
  },
  {
    category: 'Analysis',
    icon: <AssessmentIcon fontSize="small" />,
    prompts: [
      { label: 'Key patterns', prompt: 'What patterns or trends do you see in this data?' },
      { label: 'Field distributions', prompt: 'Show me the distribution of values across the main fields' },
      { label: 'Notable outliers', prompt: 'Are there any outliers or anomalies in the data?' },
    ],
  },
  {
    category: 'Filtering',
    icon: <FilterIcon fontSize="small" />,
    prompts: [
      { label: 'Recommended filters', prompt: 'What filters would you recommend to explore this data?' },
      { label: 'Top categories', prompt: 'What are the most common categories and how should I filter them?' },
    ],
  },
  {
    category: 'Spatial',
    icon: <MapIcon fontSize="small" />,
    prompts: [
      { label: 'Spatial distribution', prompt: 'Describe the spatial distribution of features' },
      { label: 'Clustering', prompt: 'Are there any geographic clusters in the data?' },
    ],
  },
];

const SpatialAnalyticsWidget: React.FC<SpatialAnalyticsWidgetProps> = ({ widgetConfig }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;
  const primaryRgb = hexToRgb(primaryColor);

  const webMapCtx = useWebMapContext();

  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [promptMenuAnchor, setPromptMenuAnchor] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const dataBinding = widgetConfig?.dataBinding;
  const layerId = dataBinding?.boundLayerId ?? '';

  const features = useMemo(() => {
    if (!webMapCtx || !layerId) return [];
    return webMapCtx.getFeaturesForLayer(layerId);
  }, [webMapCtx, layerId]);

  const allFeatures = useMemo(() => {
    if (!webMapCtx || !layerId) return [];
    return webMapCtx.layerFeatures.get(layerId) ?? [];
  }, [webMapCtx, layerId]);

  const layerLabel = useMemo(() => {
    if (!webMapCtx || !layerId) return 'No layer bound';
    const layer = webMapCtx.layers.find((l) => l.id === layerId);
    return layer ? `${layer.schema}.${layer.table}` : layerId;
  }, [webMapCtx, layerId]);

  const buildContext = useCallback(() => {
    const fieldSummaries = webMapCtx
      ? buildFieldSummaries(features, webMapCtx.layerSchemas as Map<string, { name: string; type: string }[]>, layerId)
      : [];

    const geomType = detectGeometryType(features);
    const extent = webMapCtx?.extent ?? null;
    const selected = webMapCtx?.selectedFeatures ?? [];

    return {
      overview: {
        layerName: layerLabel,
        totalFeatures: allFeatures.length,
        visibleFeatures: features.length,
        geometryType: geomType,
        bounds: extent
          ? { north: extent.north, south: extent.south, east: extent.east, west: extent.west }
          : null,
      },
      fieldSummaries,
      selectedFeatures: selected.slice(0, 20).map((f) => ({
        properties: f.properties,
      })),
      userInstructions: widgetConfig?.spatialInstructions || undefined,
    };
  }, [features, allFeatures, webMapCtx, layerId, layerLabel, widgetConfig?.spatialInstructions]);

  const handleAsk = useCallback(
    async (overrideQuestion?: string) => {
      const q = overrideQuestion ?? question;
      if (!q.trim() && chatHistory.length > 0) return;
      setLoading(true);

      if (q.trim()) {
        setChatHistory((prev) => [...prev, { role: 'user', content: q, timestamp: new Date() }]);
      }
      setQuestion('');

      try {
        const ctx = buildContext();
        const res = await fetch('/api/spatial-chat/ask', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
          },
          body: JSON.stringify({ question: q || null, context: ctx }),
        });

        const data = await res.json().catch(() => null);
        if (res.ok && data) {
          setChatHistory((prev) => [...prev, { role: 'assistant', content: data.answer, timestamp: new Date() }]);
          setSuggestions(data.suggestions ?? []);

          const action = tryParseAction(data.answer);
          if (action && webMapCtx && layerId) {
            if (action.action === 'filter' && typeof action.field === 'string' && action.value !== undefined) {
              webMapCtx.applyFilter(layerId, `ai_${action.field}`, ['==', ['get', action.field], action.value]);
            }
            if (action.action === 'clear_filters') {
              webMapCtx.removeFilter(layerId, `ai_${action.field as string}`);
            }
          }
        } else {
          const detail = typeof data?.detail === 'string'
            ? data.detail
            : 'Unable to get a response from Azure OpenAI. Please try again.';
          setChatHistory((prev) => [
            ...prev,
            { role: 'assistant', content: detail, timestamp: new Date() },
          ]);
          setSuggestions([]);
        }
      } catch {
        setChatHistory((prev) => [
          ...prev,
          { role: 'assistant', content: 'Connection error. Please check your network.', timestamp: new Date() },
        ]);
      } finally {
        setLoading(false);
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    },
    [question, chatHistory.length, buildContext, webMapCtx, layerId],
  );

  const colors = useMemo(
    () => ({
      background: isDark
        ? 'linear-gradient(135deg, rgba(30, 32, 48, 0.95) 0%, rgba(20, 22, 35, 0.98) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
      cardBg: isDark ? 'rgba(40, 44, 62, 0.6)' : 'rgba(255, 255, 255, 0.9)',
      border: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
      text: isDark ? '#ffffff' : '#1a1a2e',
      textSecondary: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
      accent: primaryColor,
      accentRgb: primaryRgb,
    }),
    [isDark, primaryColor, primaryRgb],
  );

  const noBound = !layerId;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: colors.background,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderBottom: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}
      >
        <AnalyticsIcon sx={{ fontSize: '1rem', color: colors.accent }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: colors.text, flex: 1 }}>
          Smart Analytics
        </Typography>
        {!noBound && (
          <Chip
            size="small"
            icon={<DataIcon sx={{ fontSize: '0.75rem !important' }} />}
            label={layerLabel}
            sx={{ height: 20, fontSize: '0.65rem', bgcolor: `rgba(${primaryRgb}, 0.12)`, color: colors.accent }}
          />
        )}
        <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
          <IconButton size="small" onClick={() => setExpanded((v) => !v)} sx={{ color: colors.textSecondary, p: 0.25 }}>
            {expanded ? <FullscreenExitIcon sx={{ fontSize: '0.9rem' }} /> : <FullscreenIcon sx={{ fontSize: '0.9rem' }} />}
          </IconButton>
        </Tooltip>
        {chatHistory.length > 0 && (
          <Tooltip title="Clear chat">
            <IconButton
              size="small"
              onClick={() => {
                setChatHistory([]);
                setSuggestions([]);
                setQuestion('');
              }}
              sx={{ color: colors.textSecondary, p: 0.25 }}
            >
              <CloseIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {noBound && (
        <Box sx={{ p: 2, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <MapIcon sx={{ fontSize: 40, color: colors.textSecondary, mb: 1, mx: 'auto' }} />
          <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mb: 0.5 }}>
            No Web Map layer bound
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary }}>
            Open widget settings and bind this widget to a Web Map layer to enable spatial AI analysis.
          </Typography>
        </Box>
      )}

      {!noBound && (
        <>
          <Box sx={{ px: 1.5, pt: 1, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.accent }}>
                Ask Sentinel
              </Typography>
              <Chip
                size="small"
                label={`${features.length} features in view`}
                sx={{ height: 18, fontSize: '0.6rem', bgcolor: `rgba(${primaryRgb}, 0.08)`, color: colors.textSecondary }}
              />
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Ask about this dataset..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !loading) handleAsk();
              }}
              disabled={loading}
              InputProps={{
                sx: {
                  bgcolor: isDark ? 'rgba(20, 22, 35, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                  color: colors.text,
                  fontSize: '0.8rem',
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Prompt Library">
                      <IconButton
                        size="small"
                        onClick={(e) => setPromptMenuAnchor(e.currentTarget)}
                        sx={{ color: promptMenuAnchor ? colors.accent : colors.textSecondary, '&:hover': { color: colors.accent } }}
                      >
                        <PromptIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => handleAsk()} disabled={loading} sx={{ color: colors.accent }}>
                      {loading ? <CircularProgress size={16} /> : <SendIcon sx={{ fontSize: '1rem' }} />}
                    </IconButton>
                  </Box>
                ),
              }}
            />

            <Menu
              anchorEl={promptMenuAnchor}
              open={Boolean(promptMenuAnchor)}
              onClose={() => setPromptMenuAnchor(null)}
              PaperProps={{
                sx: {
                  bgcolor: isDark ? 'rgba(30, 32, 48, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 2,
                  maxHeight: 400,
                  minWidth: 280,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                },
              }}
            >
              <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${colors.border}` }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: colors.text }}>Spatial Prompts</Typography>
                <Typography sx={{ fontSize: '0.65rem', color: colors.textSecondary }}>Click to auto-fill</Typography>
              </Box>
              {PREDEFINED_PROMPTS.map((cat, ci) => (
                <Box key={ci}>
                  <Box sx={{ px: 2, py: 0.75, bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)' }}>
                    <Typography
                      sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.accent, display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {cat.icon}
                      {cat.category}
                    </Typography>
                  </Box>
                  {cat.prompts.map((p, pi) => (
                    <MenuItem
                      key={pi}
                      onClick={() => {
                        setQuestion(p.prompt);
                        setPromptMenuAnchor(null);
                      }}
                      sx={{
                        fontSize: '0.75rem',
                        color: colors.text,
                        py: 0.75,
                        '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
                      }}
                    >
                      <ListItemText primary={p.label} primaryTypographyProps={{ fontSize: '0.75rem' }} />
                    </MenuItem>
                  ))}
                </Box>
              ))}
            </Menu>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              px: 1.5,
              py: 1,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': {
                background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                borderRadius: 4,
              },
            }}
          >
            {chatHistory.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <AnalyticsIcon sx={{ fontSize: 32, color: colors.textSecondary, mb: 1 }} />
                <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>
                  Ask a question about <strong>{layerLabel}</strong> or use the prompt library to get started.
                </Typography>
              </Box>
            )}

            {chatHistory.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  bgcolor:
                    msg.role === 'user'
                      ? `rgba(${primaryRgb}, 0.08)`
                      : isDark
                        ? 'rgba(20, 22, 35, 0.6)'
                        : 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 1.5,
                  border: `1px solid ${msg.role === 'user' ? `rgba(${primaryRgb}, 0.2)` : colors.border}`,
                }}
              >
                <Typography
                  sx={{ fontSize: '0.65rem', color: colors.textSecondary, mb: 0.5, fontWeight: 600 }}
                >
                  {msg.role === 'user' ? 'You' : 'Sentinel'}
                </Typography>
                <Typography
                  sx={{
                    color: colors.text,
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    '& strong': { color: colors.accent },
                  }}
                >
                  {renderChatContent(msg.content)}
                </Typography>
              </Box>
            ))}

            {loading && (
              <Box sx={{ textAlign: 'center', py: 1 }}>
                <CircularProgress size={20} sx={{ color: colors.accent }} />
                <Typography sx={{ color: colors.textSecondary, fontSize: '0.75rem', mt: 0.5 }}>Analysing spatial data...</Typography>
              </Box>
            )}

            {suggestions.length > 0 && chatHistory.length > 0 && !loading && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {suggestions.slice(0, 4).map((s, i) => (
                  <Chip
                    key={i}
                    label={s}
                    size="small"
                    onClick={() => {
                      setQuestion(s);
                      handleAsk(s);
                    }}
                    sx={{
                      fontSize: '0.6rem',
                      height: 20,
                      cursor: 'pointer',
                      bgcolor: `rgba(${primaryRgb}, 0.1)`,
                      color: colors.accent,
                      '&:hover': { bgcolor: `rgba(${primaryRgb}, 0.2)` },
                    }}
                  />
                ))}
              </Box>
            )}

            <div ref={chatEndRef} />
          </Box>
        </>
      )}
    </Box>
  );
};

export default SpatialAnalyticsWidget;
